import { isSSN } from "../../../services/utils";
import {fetchPersonBySSN, INameParts} from "./PepWatchApi";
import { CountryList } from "../../../assets/staticData/CountryList";
import { parsedFieldData, RowError } from "../../../types/PepTypes";

// Enhanced rate limiting utility with better error handling
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Circuit breaker pattern for API failures
class CircuitBreaker {
  private failures = 0;
  private lastFailureTime = 0;
  private readonly threshold = 5; // Max failures before opening circuit
  private readonly timeout = 30000; // 30 seconds before trying again

  isOpen(): boolean {
    if (this.failures >= this.threshold) {
      if (Date.now() - this.lastFailureTime > this.timeout) {
        this.failures = 0; // Reset circuit breaker
        return false;
      }
      return true;
    }
    return false;
  }

  recordFailure(): void {
    this.failures++;
    this.lastFailureTime = Date.now();
  }

  recordSuccess(): void {
    this.failures = 0;
  }
}

// Global circuit breaker for SSN validation API
const ssnApiCircuitBreaker = new CircuitBreaker();

// Batch processing utility with enhanced abort support and error recovery
const processBatchWithAbort = async <T, R>(
  items: T[],
  processor: (item: T, index: number) => Promise<R>,
  batchSize: number = 5,
  delayBetweenBatches: number = 1000,
  onProgress?: (completed: number, total: number) => void,
  abortSignal?: AbortSignal
): Promise<R[]> => {
  const results: R[] = [];
  let completedCount = 0;
  
  for (let i = 0; i < items.length; i += batchSize) {
    // Check for abort before each batch
    if (abortSignal?.aborted) {
      throw new DOMException('Yfirlestur stöðvaður af notanda', 'AbortError');
    }
  
    const batch = items.slice(i, i + batchSize);
  
    // Use a local variable to track completed items in this batch
    let batchCompleted = 0;
  
    // Process batch with error isolation - one failed item won't break the whole batch
    const batchPromises = batch.map((item, batchIndex) => {
      // Return a function that does not close over completedCount directly
      return (async () => {
        try {
          // Check for abort before processing each item
          if (abortSignal?.aborted) {
            throw new DOMException('Yfirlestur stöðvaður af notanda', 'AbortError');
          }
  
          const result = await processor(item, i + batchIndex);
          batchCompleted++;
  
          // Update progress with throttling to prevent excessive calls
          // (Progress is updated after the batch below)
          return result;
        } catch (error) {
          // Re-throw abort errors immediately
          if (error instanceof DOMException && error.name === 'AbortError') {
            throw error;
          }
  
          // Log other errors but don't break the batch
          console.error(`Error processing item ${i + batchIndex}:`, error);
          batchCompleted++;
          // (Progress is updated after the batch below)
          // Return a failed result instead of breaking
          throw error; // Still throw to handle at higher level
        }
      })();
    });
  
    try {
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
    } catch (error) {
      // If it's an abort error, propagate it
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
  
      // For other errors, we've already logged them, continue with next batch
      console.warn(`Batch ${Math.floor(i / batchSize)} had errors, continuing...`);
    }
  
    // Update the global completedCount and progress after the batch
    completedCount += batchCompleted;
    onProgress?.(completedCount, items.length);
  
    // Add delay between batches (except for the last batch)
    if (i + batchSize < items.length) {
      // Check for abort before delay
      if (abortSignal?.aborted) {
        throw new DOMException('Yfirlestur stöðvaður af notanda', 'AbortError');
      }
      await delay(delayBetweenBatches);
    }
  }
  
  return results;
};

// Enhanced cache with memory management for large datasets
class SSNCache {
  private cache = new Map<string, INameParts | null>();
  private accessOrder: string[] = [];
  private readonly maxSize = 1000; // Limit cache size to prevent memory issues

  get(ssn: string): INameParts | null | undefined {
    const result = this.cache.get(ssn);
    if (result !== undefined) {
      // Move to end (most recently used)
      this.accessOrder = this.accessOrder.filter(key => key !== ssn);
      this.accessOrder.push(ssn);
    }
    return result;
  }

  set(ssn: string, value: INameParts | null): void {
    // Remove oldest entries if cache is full
    if (this.cache.size >= this.maxSize && !this.cache.has(ssn)) {
      const oldestKey = this.accessOrder.shift();
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(ssn, value);
    
    // Update access order
    this.accessOrder = this.accessOrder.filter(key => key !== ssn);
    this.accessOrder.push(ssn);
  }

  has(ssn: string): boolean {
    return this.cache.has(ssn);
  }

  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
  }

  size(): number {
    return this.cache.size;
  }
}

// Enhanced SSN cache instance
const ssnCache = new SSNCache();

// Enhanced SSN validation with circuit breaker and better error handling
const validateSSNWithCache = async (
  ssn: string, 
  abortSignal?: AbortSignal
): Promise<INameParts | null> => {
  
  // Check cache first
  if (ssnCache.has(ssn)) {
    return ssnCache.get(ssn)!;
  }
  
  // Check circuit breaker
  if (ssnApiCircuitBreaker.isOpen()) {
    throw new Error('SSN validation service temporarily unavailable due to repeated failures');
  }
  
  // Check for abort
  if (abortSignal?.aborted) {
    throw new DOMException('Aðgerð hætt við', 'AbortError');
  }
  
  try {
    const personData = await fetchPersonBySSN(ssn);
    
    // Record success in circuit breaker
    ssnApiCircuitBreaker.recordSuccess();
    
    // Cache the result
    ssnCache.set(ssn, personData);
    
    return personData;
  } catch (error) {
    // Check if it was an abort
    if (abortSignal?.aborted) {
      throw new DOMException('Aðgerð hætt við', 'AbortError');
    }
    
    // Record failure in circuit breaker
    ssnApiCircuitBreaker.recordFailure();
    
    // Don't cache errors, allow retry
    throw error;
  }
};

// Enhanced row processing with better error isolation
const processRowWithAPI = async (
  row: any, 
  index: number,
  validRows: parsedFieldData[],
  invalidRows: RowError[],
  abortSignal?: AbortSignal
): Promise<void> => {
  const fieldErrors: Record<string, string> = {};
  const displayRowIndex = index + 2; // Spreadsheet row (1-indexed + header row)
  let retryCount = 0;
  let personData: INameParts | null = null;
  
  // Create working copy of row data
  const workingRow = { ...row };

  const MAX_RETRIES = 3;
  const RETRY_DELAYS = [1000, 2000, 4000]; // Progressive backoff

  // Step 1: Validate PersonId (we know it exists and has valid format if we're here)
  let isPersonIdValid = false;

  // PersonId format is valid, now check in the database with enhanced retry logic
  while (retryCount < MAX_RETRIES) {
    // Check for abort before API call
    if (abortSignal?.aborted) {
      throw new DOMException('Notandi hætti við yfirlestur gagna', 'AbortError');
    }
    
    try {
      personData = await validateSSNWithCache(workingRow.PersonId.toString(), abortSignal);
      
      if (!personData) {
        fieldErrors.PersonId = "Kennitala ekki rétt";
        isPersonIdValid = false;
      } else {
        isPersonIdValid = true;
        
        // Auto-populate missing fields from PersonId data for better UX
        if (!workingRow.Name && personData) {
          const fullName = [
            personData.firstName,
            personData.middleName,
            personData.lastName
          ].filter(Boolean).join(' ');
          workingRow.Name = fullName;
        }
      }
      break; // Success, exit retry loop
      
    } catch (error) {
      // Check if it's an abort error
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      
      retryCount++;
      
      if (retryCount >= MAX_RETRIES) {
        // After all retries failed, mark as validation error but continue processing
        fieldErrors.PersonId = "Villa við að staðfesta kennitölu - reyndu aftur síðar";
        isPersonIdValid = false;
        console.error(`Failed to validate SSN after ${MAX_RETRIES} retries:`, error);
      } else {
        // Wait before retrying with progressive backoff
        const delayMs = RETRY_DELAYS[retryCount - 1];
        console.warn(`SSN validation attempt ${retryCount} failed, retrying in ${delayMs}ms:`, error);
        await delay(delayMs);
      }
    }
  }

  // Continue with rest of validation regardless of API success/failure
  const result = validateRowFields(workingRow, fieldErrors, isPersonIdValid, personData);
  
  if (result.isValid) {
    validRows.push(result.enrichedRow);
  } else {
    invalidRows.push({
      rowIndex: displayRowIndex,
      fieldErrors: result.fieldErrors,
      originalRow: workingRow,
    });
  }
};

// Process a single row without API calls (for rows that don't need PersonId validation)
const processRowWithoutAPI = (
  row: any, 
  index: number,
  validRows: parsedFieldData[],
  invalidRows: RowError[]
): void => {
  const fieldErrors: Record<string, string> = {};
  const displayRowIndex = index + 2; // Spreadsheet row (1-indexed + header row)
  
  // Create working copy of row data
  const workingRow = { ...row };

  // Check if PersonId exists but is invalid format
  if (workingRow.PersonId) {
    fieldErrors.PersonId = "Kennitala ekki rétt";
  }

  // Continue with rest of validation (no PersonId is valid for this path)
  const result = validateRowFields(workingRow, fieldErrors, false, null);
  
  if (result.isValid) {
    validRows.push(result.enrichedRow);
  } else {
    invalidRows.push({
      rowIndex: displayRowIndex,
      fieldErrors: result.fieldErrors,
      originalRow: workingRow,
    });
  }
};

// Enhanced field validation with better error messages and validation logic
const validateRowFields = (
  workingRow: any,
  fieldErrors: Record<string, string>,
  isPersonIdValid: boolean,
  personData: INameParts | null
) => {
  // Step 2: Validate Name, DateOfBirth, CountryCode for sanctions
  let hasSanctionData = true;

  // Validate Name with enhanced checks
  if (!workingRow.Name || typeof workingRow.Name !== "string" || workingRow.Name.trim().length === 0) {
    if (!isPersonIdValid) {
      fieldErrors.Name = "Nafn vantar";
    }
    hasSanctionData = false;
  } else {
    const trimmedName = workingRow.Name.trim();
    const nameParts = trimmedName.split(/\s+/).filter((part: string | any[]) => part.length > 0);
    
    if (nameParts.length < 2) {
      fieldErrors.Name = "Nafn þarf að innihalda bæði fornafn og eftirnafn";
      hasSanctionData = false;
    } else if (trimmedName.length > 200) { // Reasonable name length limit
      fieldErrors.Name = "Nafn er of langt";
      hasSanctionData = false;
    }
    
    // Update the working row with trimmed name
    workingRow.Name = trimmedName;
  }

  // Validate DateOfBirth with enhanced date validation
  if (!workingRow.DateOfBirth) {
    if (!isPersonIdValid) {
      fieldErrors.DateOfBirth = "Fæðingardag vantar";
    }
    hasSanctionData = false;
  } else {
    // Check format using regex (YYYY-MM-DD)
    const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateFormatRegex.test(workingRow.DateOfBirth)) {
      fieldErrors.DateOfBirth = "Fæðingardagur þarf að vera á forminu YYYY-MM-DD";
      hasSanctionData = false;
    } else {
      const birthDate = new Date(workingRow.DateOfBirth);
      const today = new Date();
      const minValidDate = new Date('1900-01-01'); // Reasonable minimum birth year
      
      // Reset time portion for accurate date comparison
      today.setHours(23, 59, 59, 999); // End of today
      
      if (isNaN(birthDate.getTime())) {
        fieldErrors.DateOfBirth = "Ógildur fæðingardagur";
        hasSanctionData = false;
      } else if (birthDate > today) {
        fieldErrors.DateOfBirth = "Fæðingardagur má ekki vera í framtíðinni";
        hasSanctionData = false;
      } else if (birthDate < minValidDate) {
        fieldErrors.DateOfBirth = "Fæðingardagur er of gamall";
        hasSanctionData = false;
      }
    }
  }

  // Validate CountryCode with enhanced validation
  if (!workingRow.CountryCode) {
    if (!isPersonIdValid) {
      fieldErrors.CountryCode = "Landakóða vantar";
    }
    hasSanctionData = false;
  } else {
    const countryCode = workingRow.CountryCode.toString().trim().toUpperCase();
    const validCountry = CountryList.some((country) => 
      country.value.toUpperCase() === countryCode
    );
    
    if (!validCountry) {
      fieldErrors.CountryCode = "Ógildur landakóði";
      hasSanctionData = false;
    } else {
      // Normalize country code to uppercase
      workingRow.CountryCode = countryCode;
    }
  }

  // Step 3: Validate Gender if provided with enhanced validation
  if (workingRow.Gender !== null && 
      workingRow.Gender !== undefined && 
      workingRow.Gender !== "") {
    
    const gender = workingRow.Gender.toString().trim();
    if (gender !== "Karl" && gender !== "Kona") {
      fieldErrors.Gender = "Ógilt kyn (þarf að vera 'Karl' eða 'Kona')";
    } else {
      workingRow.Gender = gender === "Karl" ? "Male" : "Female";
    }
  }

  // Step 4: Set frequencies based on what data is available and valid
  if (isPersonIdValid) {
    workingRow.PepFrequency = workingRow.PepFrequency || "daily";
  }

  if (hasSanctionData && Object.keys(fieldErrors).length === 0) {
    workingRow.SanctionFrequency = workingRow.SanctionFrequency || "daily";
  }

  // Step 5: Determine if row is valid (at least one frequency can be set)
  const isRowValid = (isPersonIdValid || hasSanctionData) && Object.keys(fieldErrors).length === 0;

  if (isRowValid) {
    // Create enriched row data for valid entries
    const enrichedRow = {
      ...workingRow,
      // Add cached person data if available
      ...(personData && {
        cachedPersonData: {
          firstName: personData.firstName,
          middleName: personData.middleName,
          lastName: personData.lastName,
        }
      }),
      // Add metadata about what's valid
      validationMeta: {
        canUsePepFrequency: isPersonIdValid,
        canUseSanctionFrequency: hasSanctionData && Object.keys(fieldErrors).length === 0,
        dataSource: isPersonIdValid ? (personData ? 'api' : 'user') : 'user',
        validatedAt: new Date().toISOString() // Add timestamp for debugging
      }
    };

    return { isValid: true, enrichedRow, fieldErrors };
  }

  return { isValid: false, enrichedRow: null, fieldErrors };
};

// Enhanced validation with better error handling and memory management
export const validateParsedData = async (
  data: any[],
  onProgress?: (current: number, total: number) => void,
  abortSignal?: AbortSignal
): Promise<{ validRows: parsedFieldData[]; invalidRows: RowError[] }> => {
  const validRows: parsedFieldData[] = [];
  const invalidRows: RowError[] = [];
  
  // Check for abort before starting
  if (abortSignal?.aborted) {
    throw new DOMException('Yfirlestri gagna var hætt áður en hann hófst', 'AbortError');
  }

  // Input validation
  if (!Array.isArray(data)) {
    throw new Error('Ógilt snið á gögnum: búist var við fylki');
  }

  if (data.length === 0) {
    onProgress?.(0, 0);
    return { validRows, invalidRows };
  }
  
  
  // Configuration with adaptive batch sizing based on data size
  const BATCH_SIZE = data.length > 5000 ? 50 : 100; // Smaller batches for large datasets
  const DELAY_BETWEEN_BATCHES = data.length > 1000 ? 50 : 100; // Faster processing for large datasets

  // Step 1: Separate rows that need API calls from those that don't
  const rowsNeedingAPI: Array<{row: any, index: number}> = [];
  const rowsNotNeedingAPI: Array<{row: any, index: number}> = [];

  // Pre-process and categorize rows
  data.forEach((row, index) => {
    // Validate that row is an object
    if (!row || typeof row !== 'object') {
      invalidRows.push({
        rowIndex: index + 2,
        fieldErrors: { almenn: 'Ógilt snið á röð' },
        originalRow: row,
      });
      return;
    }

    const hasValidPersonId = row.PersonId && isSSN(row.PersonId.toString());
    
    if (hasValidPersonId) {
      rowsNeedingAPI.push({ row, index });
    } else {
      rowsNotNeedingAPI.push({ row, index });
    }
  });

  let processedCount = 0;

  // Step 2: Process rows that don't need API calls with smart progress updates
  const processNonAPIRowsWithProgressUpdates = async () => {
    const totalNonAPIRows = rowsNotNeedingAPI.length;
    let lastProgressUpdate = 0;
    let lastUIUpdate = Date.now();
    
    // Calculate optimal update intervals based on data size
    const progressUpdateInterval = Math.max(1, Math.floor(totalNonAPIRows / 100)); // Update progress every 1% or at least every row
    const uiUpdateInterval = 100; // Force UI update every 100ms for large datasets
    
    for (let i = 0; i < totalNonAPIRows; i++) {
      const { row, index } = rowsNotNeedingAPI[i];
      
      // Check for abort
      if (abortSignal?.aborted) {
        throw new DOMException('Hætt var við yfirlestur gagna í non-API vinnslu', 'AbortError');
      }
      
      try {
        processRowWithoutAPI(row, index, validRows, invalidRows);
      } catch (error) {
        console.error(`Error processing non-API row ${index}:`, error);
        // Add to invalid rows instead of breaking
        invalidRows.push({
          rowIndex: index + 2,
          fieldErrors: { general: 'Óþekkt villa kom upp' },
          originalRow: row,
        });
      }
      
      processedCount++;
      
      // Smart progress update strategy
      const shouldUpdateProgress = 
        (processedCount - lastProgressUpdate >= progressUpdateInterval) || // Regular interval updates
        (processedCount === data.length) || // Always update on completion
        (Date.now() - lastUIUpdate >= uiUpdateInterval); // Force update every 100ms for large datasets
      
      if (shouldUpdateProgress) {
        lastProgressUpdate = processedCount;
        lastUIUpdate = Date.now();
        
        // Update progress immediately
        onProgress?.(processedCount, data.length);
        
        // For very large datasets, add micro-delays to ensure UI responsiveness
        if (totalNonAPIRows > 1000) {
          // Yield control to browser every 500 items or every 100ms, whichever comes first
          if (processedCount % 500 === 0 || Date.now() - lastUIUpdate >= 100) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        } else if (totalNonAPIRows > 100) {
          // For medium datasets, yield control less frequently
          if (processedCount % 100 === 0) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }
      }
    }
    
    // Ensure final progress update
    onProgress?.(processedCount, data.length);
  };

  await processNonAPIRowsWithProgressUpdates();

  // Step 3: Process rows that need API calls in batches
  if (rowsNeedingAPI.length > 0) {
    try {
      await processBatchWithAbort(
        rowsNeedingAPI,
        async ({ row, index }) => {
          await processRowWithAPI(row, index, validRows, invalidRows, abortSignal);
        },
        BATCH_SIZE,
        DELAY_BETWEEN_BATCHES,
        (completed, total) => {
          // Add the non-API rows already processed to get true total progress
          const totalProgress = processedCount + completed;
          onProgress?.(totalProgress, data.length);
        },
        abortSignal
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      
      console.error('Error during API validation:', error);
      // Don't throw - we want to return partial results
    }
  }
  
  // Only clear cache if it's large to prevent unnecessary work for subsequent validations
  if (ssnCache.size() > 500) {
    ssnCache.clear();
  }

  return { validRows, invalidRows };
};